<!-- AI应用 — 业务模块快捷入口 -->
<template>
  <div class="ai-hub">
    <div v-loading="loading" class="module-grid">
      <button
        v-for="item in modules"
        :key="item.key"
        class="module-card"
        type="button"
        :style="{ '--module-color': item.color }"
        @click="goPath(item)"
      >
        <div
          class="module-icon"
          :style="{
            background: item.iconUrl ? 'transparent' : hexToRgba(item.color, 0.1),
            color: item.color
          }"
        >
          <ModuleCardIcon :icon-url="item.iconUrl" :icon="item.icon" :size="22" />
        </div>
        <div class="module-content">
          <div class="module-name">{{ item.label }}</div>
          <div class="module-desc">{{ item.desc }}</div>
        </div>
        <Icon class="module-arrow" icon="ep:arrow-right" :size="16" />
      </button>
    </div>
    <el-empty v-if="!loading && modules.length === 0" description="暂无已开通应用">
      <el-button type="primary" @click="router.push('/products')">前往产品中心</el-button>
    </el-empty>
  </div>
</template>

<script lang="ts" setup>
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import * as ModuleApi from '@/api/system/module'
import { getTenantProducts } from '@/api/system/tenantModule'
import { usePermissionStore } from '@/store/modules/permission'
import { launchStandaloneAppAsync, resolveStandaloneAppKey } from '@/utils/standaloneApp'
import { buildModuleCards, type ModuleCard } from '@/utils/moduleCard'
import { AI_APP_CENTER_MODULES } from '@/constants/aiAppCenter'
import { ModuleCardIcon } from '@/components/ModuleCardIcon'

defineOptions({ name: 'AiApplicationCenter' })

const router = useRouter()
const permissionStore = usePermissionStore()
const loading = ref(false)
const modules = ref<ModuleCard[]>([])
const expiredModules = ref(new Set<string>())

const loadModules = async () => {
  loading.value = true
  try {
    const [tenantProductsSettled, availableSettled, enabledSettled] = await Promise.allSettled([
      getTenantProducts(),
      ModuleApi.getAvailableModuleList(),
      ModuleApi.getEnabledModuleList()
    ])
    const tenantProducts =
      tenantProductsSettled.status === 'fulfilled' ? tenantProductsSettled.value || [] : []
    const availableModules =
      availableSettled.status === 'fulfilled' ? availableSettled.value || [] : []
    const enabledModules = enabledSettled.status === 'fulfilled' ? enabledSettled.value || [] : []
    if (tenantProductsSettled.status === 'rejected') {
      console.warn('[AiApplicationCenter] getTenantProducts failed', tenantProductsSettled.reason)
    }
    // 外部租户管理员常误挂 super_admin：勿据此放开菜单校验，只展示已开通且菜单有权的应用
    const validCards = buildModuleCards(
      availableModules,
      'ai-app',
      permissionStore.getMenuRouters,
      permissionStore.getRouters,
      false
    )
    const expiredCodes = new Set(
      tenantProducts
        .filter((item) => item.tenantStatus === 'EXPIRED')
        .map((item) => item.moduleCode)
    )
    const expiredCards = buildModuleCards(
      enabledModules.filter((item) => expiredCodes.has(item.name)),
      'ai-app',
      permissionStore.getMenuRouters,
      permissionStore.getRouters,
      true
    )
    // 双码：过期码是库表 name，卡片 key 是独立窗 key
    const expiredKeys = new Set(expiredCodes)
    if (expiredKeys.has('o2o')) {
      expiredKeys.add('visiting')
    }
    if (expiredKeys.has('capacity')) {
      expiredKeys.add('distribution')
    }
    expiredModules.value = expiredKeys
    modules.value = [...validCards, ...expiredCards]
  } finally {
    loading.value = false
  }
}

function hexToRgba(hex: string, alpha: number) {
  if (!hex) return `rgba(108,92,231,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

/** 兜底：库表 homePath 残缺时仍能进 AI 教育，避免 window.open(about:blank) */
const MODULE_ENTRY_FALLBACK: Record<string, string> = {
  aiEducation: '/ai-Application-center/ai-education/overview',
  'ai-education': '/ai-Application-center/ai-education/overview'
}

function resolveEntryTarget(item: ModuleCard) {
  const resolvedKey = resolveStandaloneAppKey(item.key)
  const raw =
    item.standalonePath ||
    item.path ||
    MODULE_ENTRY_FALLBACK[item.key] ||
    MODULE_ENTRY_FALLBACK[resolvedKey] ||
    ''
  const target = String(raw).trim()
  if (!target || target === 'about:blank' || target === '/' || target === 'null' || target === 'undefined') {
    return MODULE_ENTRY_FALLBACK[resolvedKey] || MODULE_ENTRY_FALLBACK[item.key] || ''
  }
  return target
}

async function goPath(item: ModuleCard) {
  if (expiredModules.value.has(item.key)) {
    ElMessage.warning('该产品已过期，请续费后再进入')
    return
  }
  // 优先用注册表独立窗入口，避免库表误配 /errand、same-window 导致 404
  const reg = AI_APP_CENTER_MODULES.find((m) => m.key === item.key)
  const target = resolveEntryTarget({
    ...item,
    standalonePath: reg?.standalonePath || item.standalonePath,
    path: reg?.homePath || item.path,
  })
  if (!target) {
    ElMessage.error('应用入口未配置，请联系管理员检查模块 homePath')
    return
  }
  if (item.openMode === 'plain') {
    // 相对路径走独立窗拼装，避免直接 open 导致空白页
    if (target.startsWith('http://') || target.startsWith('https://')) {
      const popup = window.open(target, '_blank')
      if (!popup) {
        ElMessage.error('浏览器阻止了新窗口，请允许本站打开弹窗后重试')
      }
      return
    }
    const opened = await launchStandaloneAppAsync(target, item.key)
    if (!opened) {
      ElMessage.error('浏览器阻止了新窗口，请允许本站打开弹窗后重试')
    }
    return
  }
  if (item.openMode === 'same-window' && !reg?.standalonePath) {
    router.push(target.startsWith('/') ? target : item.path)
    return
  }
  try {
    const opened = await launchStandaloneAppAsync(target, item.key)
    if (!opened) {
      ElMessage.error('浏览器阻止了新窗口，请允许本站打开弹窗后重试')
    }
  } catch {
    ElMessage.error('打开独立窗失败，请稍后重试')
  }
}

onMounted(loadModules)
</script>

<style lang="scss" scoped>
.ai-hub {
  min-height: 100%;
  padding: 2px 0 24px;
  background: transparent;
}

.hub-header {
  margin-bottom: 20px;

  h1 {
    margin-bottom: 4px;
    font-size: 22px;
    font-weight: 700;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #6b719c;
  }
}

.section-title {
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 600;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (width <= 1120px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (width <= 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (width <= 600px) {
    grid-template-columns: 1fr;
  }
}

.module-card {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 18px;
  min-height: 108px;
  padding: 18px;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: var(--app-card-bg-color, #fff);
  border: 1px solid var(--app-card-border-color, #eceef2);
  border-radius: 12px;
  box-shadow: none;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: var(--module-color);
    background: #fafcfe;
    box-shadow: none;

    .module-arrow {
      color: var(--module-color);
      transform: translateX(2px);
    }
  }

  &:focus-visible {
    outline: 2px solid #409eff;
    outline-offset: 2px;
  }
}

.module-icon {
  position: relative;
  display: flex;
  width: 46px;
  height: 46px;
  overflow: hidden;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.module-content {
  min-width: 0;
}

.module-name {
  margin-bottom: 6px;
  font-size: 15px;
  font-weight: 600;
  line-height: 21px;
  color: #1f2937;
}

.module-desc {
  display: -webkit-box;
  overflow: hidden;
  font-size: 12px;
  line-height: 18px;
  color: #8491a5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.module-arrow {
  color: #b7c0cb;
  transition:
    color 0.18s ease,
    transform 0.18s ease;
}
</style>
